import {
  OrderPosition,
  OrderTable,
  Position,
  ServingMethod,
  Table,
} from '@core/hera-models';

/**
 * A "placement" is where the guest is seated for a CAT order:
 * - a {@link Table} for the sequential service
 * - a {@link Position} for the complete service
 * - nothing for the counter pickup service
 */
export type PlacementKind = 'table' | 'position';

/** Normalized, serving-method-agnostic view of a table or a position. */
export interface Placement {
  kind: PlacementKind;
  id: string;
  number: string;
  isOrderable: boolean;
}

/** Payload fragment sent to Hera when creating a quotation or an order. */
export type PlacementPayload =
  | { table: OrderTable; position?: undefined }
  | { position: OrderPosition; table?: undefined }
  | Record<string, never>;

export function placementKindForServingMethod(
  servingMethod: ServingMethod | string | null | undefined
): PlacementKind | null {
  switch (servingMethod) {
    case ServingMethod.CompleteService:
      return 'position';
    case ServingMethod.SequentialService:
      return 'table';
    default:
      return null;
  }
}

export function tableToPlacement(table: Table): Placement {
  return {
    kind: 'table',
    id: table.id,
    number: table.number,
    isOrderable: table.is_orderable,
  };
}

export function positionToPlacement(position: Position): Placement {
  return {
    kind: 'position',
    id: position.id,
    number: position.number,
    isOrderable: position.is_orderable,
  };
}

export function placementToPayload(
  placement: Placement | null
): PlacementPayload {
  if (!placement) {
    return {};
  }
  const value = { id: placement.id, number: placement.number };
  return placement.kind === 'position' ? { position: value } : { table: value };
}
